import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import {
    Paper, Typography, Box,
    Card, CardContent, Divider,
    Stack, IconButton, Grid,
    Chip, Avatar, Table,
    TableBody, TableCell, TableContainer,
    TableHead, TableRow, Accordion,
    AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StopIcon from '@mui/icons-material/Stop';
import EventIcon from '@mui/icons-material/Event';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RouteIcon from '@mui/icons-material/Route';
import BusAlertIcon from '@mui/icons-material/BusAlert';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { selectedTripSelector, setShowTripReport, showTripReportSelector, tripReportPdf, tripReportPdfLoadingSelector } from '../../../features/tripcenter/tripControlSlice';
import Icon from '@mdi/react';
import { mdiCloseCircleOutline, mdiFilePdfBox } from '@mdi/js';
import { tripDateView, tripDurationFormat, tripShortDateView, tripHoursFormat } from '../../../utils/dateFormat';
import { ruleNames, ruleLimitFormatters } from '../../../utils/ruleNames';
import eventIcons from '../../../utils/eventIcons';
import RouteDetailView from '../routeViewComponents/RouteDetailView';
import { selectSelectedRoute, setSelectedRoute } from '../../../features/tripcenter/routeSlice';

const TripReport = () => {
    const tripData = useSelector(selectedTripSelector);
    const showTripReport = useSelector(showTripReportSelector);
    const tripReportPdfLoading = useSelector(tripReportPdfLoadingSelector);
    const selectedRoute = useSelector(selectSelectedRoute);
    const dispatch = useDispatch();
    const [routeDetailOpen, setRouteDetailOpen] = useState(false);

    const routeData = tripData?.route;
    const routeName = (routeData?.routeName || 'N/A').split('|')[0].trim();
    const routeDisplayName = routeData?.routeNumber !== undefined && routeData?.routeNumber !== null
        ? `Route #${routeData.routeNumber} — ${routeName}`
        : routeName;
    const routeForDetail = selectedRoute?._id === routeData?._id ? selectedRoute : routeData;

    const formatRouteTimestamp = (timestamp) => {
        if (!timestamp) {
            return 'N/A';
        }

        const istDate = moment.utc(timestamp).utcOffset('+05:30');
        return istDate.isValid() ? istDate.format('Do MMMM, YYYY') : 'N/A';
    };

    const getRouteStatusSubtitle = () => {
        const archivedRoutes = Array.isArray(routeData?.archivedRoutes) ? routeData.archivedRoutes : [];
        if (archivedRoutes.length === 0) {
            return `Created - ${formatRouteTimestamp(routeData?.createdAt)}`;
        }

        const latestRevision = archivedRoutes.reduce((latest, archivedRoute) => {
            if (!archivedRoute?.archivedAt) {
                return latest;
            }
            if (!latest) {
                return archivedRoute;
            }
            return moment.utc(archivedRoute.archivedAt).isAfter(moment.utc(latest.archivedAt))
                ? archivedRoute
                : latest;
        }, null);

        return `Revised - ${formatRouteTimestamp(latestRevision?.archivedAt)}`;
    };

    const locTypeFormatter = (locType) => {
        switch (locType) {
            case 'viaLocation':
                return 'Stoppage';
            case 'startLocation':
                return 'Start';
            case 'endLocation':
                return 'End';
            default:
                return locType;
        }
    }

    const formatRuleLimit = (eventName) => {
        const formatter = ruleLimitFormatters[eventName];
        if (formatter) {
            return formatter(tripData?.rules);
        }
        const ruleValue = tripData?.rules?.[eventName];
        return ruleValue !== undefined && ruleValue !== null ? ruleValue : '-';
    };

    useEffect(() => {
        return () => {
            console.log("unmounting trip report");
            dispatch(setShowTripReport(false));
        }
    }, [dispatch]);

    const handleClose = () => {
        dispatch(setShowTripReport(false));
    }

    const handleDownloadPdf = () => {
        if (!tripData) {
            return;
        }
        dispatch(tripReportPdf({
            _id: tripData?._id,
            tripId: tripData?.tripId,
            deviceImei: tripData?.deviceImei,
            erpSystemId: tripData?.erpSystemId,
        }));
    }

    const handleRouteDetailOpen = () => {
        if (!routeData) {
            return;
        }

        dispatch(setSelectedRoute(routeData));
        setRouteDetailOpen(true);
    };

    const handleRouteDetailClose = () => {
        setRouteDetailOpen(false);
    };

    const ruleViolations = (tripData?.significantEvents || []).filter(
        (event) => event.eventType === 'ruleViolation'
    );

    const formatValue = (value, unit, decimals = 1) => {
        if (value === undefined || value === null || Number.isNaN(value)) {
            return '-';
        }
        if (typeof value === 'number') {
            const formatted = Number.isInteger(value) ? value : value.toFixed(decimals);
            return unit ? `${formatted} ${unit}` : `${formatted}`;
        }
        return unit ? `${value} ${unit}` : `${value}`;
    };

    const formatHoursValue = (hours) => {
        if (hours === undefined || hours === null || Number.isNaN(hours)) {
            return '-';
        }
        return tripHoursFormat(hours);
    };

    const formatDurationMinutes = (minutes) => {
        if (minutes === undefined || minutes === null || Number.isNaN(minutes)) {
            return '-';
        }
        return tripDurationFormat(minutes);
    };

    const formatTimeRange = (startTime, endTime) => {
        if (!startTime && !endTime) {
            return '-';
        }
        if (startTime && endTime) {
            return `${tripDateView(startTime)} to ${tripDateView(endTime)}`;
        }
        return tripDateView(startTime || endTime);
    };

    const formatRouteDiff = (diffValue) => {
        if (diffValue === undefined || diffValue === null || Number.isNaN(diffValue)) {
            return null;
        }
        if (diffValue === 0) {
            return { label: 'Route Diff', value: formatValue(0, 'km', 1) };
        }
        const label = diffValue < 0 ? 'Short Cut' : 'Extra Travel';
        return {
            label,
            value: formatValue(Math.abs(diffValue), 'km', 1),
        };
    };

    const ruleIconMap = {
        speed: <SpeedIcon sx={{ fontSize: 18, color: '#f5576c' }} />,
        routeViolation: <RouteIcon sx={{ fontSize: 18, color: '#f5576c' }} />,
        maxHalt: <StopIcon sx={{ fontSize: 18, color: '#f5576c' }} />,
        temperature: <EventIcon sx={{ fontSize: 18, color: '#f5576c' }} />,
        nightDriving: <AccessTimeIcon sx={{ fontSize: 18, color: '#f5576c' }} />,
        fuelEfficiency: <DirectionsCarIcon sx={{ fontSize: 18, color: '#f5576c' }} />,
        fuelConsumption: <DirectionsCarIcon sx={{ fontSize: 18, color: '#f5576c' }} />,
    };

    const getRuleStats = (violation) => {
        const stats = [];
        const eventDistanceKm = violation?.eventDistanceKm ?? violation?.eventDistance;

        if (violation?.eventName === 'routeViolation') {
            stats.push({
                label: 'Truck Travel',
                value: formatValue(eventDistanceKm, 'km', 1),
            });
            stats.push({
                label: 'Route Distance',
                value: formatValue(violation?.routeDistanceKm, 'km', 1),
            });
            const diffStat = formatRouteDiff(violation?.violationDifferenceKm);
            if (diffStat) {
                stats.push(diffStat);
            }
            stats.push({
                label: 'Max Deviation',
                value: formatValue(violation?.maxViolationKm, 'km', 1),
            });
            stats.push({
                label: 'Max Allowed Deviation',
                value: formatValue(tripData?.rules?.routeViolationThreshold, 'km', 1),
            });
            return stats;
        }

        if (violation?.eventName === 'maxHalt') {
            const totalHaltValue = violation?.haltDurationAtEnd !== undefined && violation?.haltDurationAtEnd !== null
                ? formatHoursValue(violation.haltDurationAtEnd)
                : formatDurationMinutes(violation?.eventDuration);

            stats.push({
                label: 'Total Halt Time',
                value: totalHaltValue,
            });
            stats.push({
                label: 'Max Allowed Halt',
                value: formatHoursValue(tripData?.rules?.maxHaltTime),
            });
            stats.push({
                label: 'Excess Halt Period',
                value: formatHoursValue(violation?.excessHaltHours),
            });
            return stats;
        }

        if (violation?.eventName === 'speed') {
            stats.push({
                label: 'Truck Travel',
                value: formatValue(eventDistanceKm, 'km', 1),
            });
            stats.push({
                label: 'Speed Limit',
                value: formatValue(tripData?.rules?.speedLimit, 'km/h', 0),
            });
            stats.push({
                label: 'Max Speed',
                value: formatValue(violation?.maxSpeed, 'km/h', 0),
            });
            stats.push({
                label: 'Average Speed',
                value: formatValue(violation?.avgSpeed, 'km/h', 0),
            });
            return stats;
        }

        if (violation?.eventName === 'nightDriving') {
            stats.push({
                label: 'Truck Travel',
                value: formatValue(eventDistanceKm, 'km', 1),
            });
            stats.push({
                label: 'Travel Duration',
                value: formatTimeRange(violation?.eventStartTime, violation?.eventEndTime),
            });
            stats.push({
                label: 'Night Driving Duration',
                value: formatDurationMinutes(violation?.eventDuration),
            });
            stats.push({
                label: 'Allowed Threshold',
                value: formatRuleLimit('nightDriving'),
            });
            return stats;
        }

        // Default stats for other rule types
        stats.push({
            label: 'Event Distance',
            value: formatValue(eventDistanceKm, 'km', 1),
        });
        if (violation?.eventName === 'temperature') {
            stats.push({
                label: 'Min Temp',
                value: formatValue(violation?.minTemp, 'C', 1),
            });
            stats.push({
                label: 'Max Temp',
                value: formatValue(violation?.maxTemp, 'C', 1),
            });
            stats.push({
                label: 'Avg Temp',
                value: formatValue(violation?.avgTemp, 'C', 1),
            });
        }

        return stats;
    };

    const getHeaderChips = (violation) => {
        const ruleType = violation?.eventName;
        const limitedRules = ['routeViolation', 'maxHalt', 'speed', 'nightDriving'];
        if (limitedRules.includes(ruleType)) {
            return [];
        }
        return [
            {
                label: `Limit: ${formatRuleLimit(ruleType)}`,
                key: 'limit',
            },
            {
                label: `Duration: ${violation?.eventDuration ? tripDurationFormat(violation.eventDuration) : '-'}`,
                key: 'duration',
            },
        ];
    };

    if (!tripData || !showTripReport) {
        return null;
    } else {
        return (
            <Paper elevation={3} sx={{
                //height: '100%',

                height: 'calc(100vh - 80px)',
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',

            }}>
                {/* Modern Header */}
                <Box sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 2,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                Trip Report
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                {tripData?.truckRegistrationNumber} • {tripShortDateView(tripData?.actualStartTime)}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <IconButton
                                onClick={handleDownloadPdf}
                                disabled={tripReportPdfLoading}
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    opacity: tripReportPdfLoading ? 0.6 : 1,
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                                }}
                            >
                                <Icon path={mdiFilePdfBox} size={1} color="white" />
                            </IconButton>
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                                }}
                            >
                                <Icon path={mdiCloseCircleOutline} size={1} color="white" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Box>

                {/* Scrollable Content */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 0, height: 'calc(100vh - 128px)' }}>
                    {/* Trip Details Section */}
                    <Box sx={{ mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
                        <Accordion defaultExpanded sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" sx={{ color: '#667eea' }}>
                                    Trip Details
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ mb: 2, color: '#667eea' }}>
                                                    Basic Information
                                                </Typography>
                                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <Box component="tbody">
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                width: '35%',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Customer:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.customer || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Goods:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.goodsName || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Trip ID:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.erpSystemId || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Driver Name:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.driverName || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Driver Contact:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.driverPhoneNumber || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ mb: 2, color: '#667eea' }}>
                                                    Route Information
                                                </Typography>
                                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <Box component="tbody">
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                width: '35%',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Route:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem',
                                                                cursor: routeData ? 'pointer' : 'default'
                                                            }}>
                                                                <Box
                                                                    role={routeData ? 'button' : undefined}
                                                                    tabIndex={routeData ? 0 : undefined}
                                                                    aria-label={routeData ? `View details for ${routeDisplayName}` : undefined}
                                                                    onClick={handleRouteDetailOpen}
                                                                    onKeyDown={(event) => {
                                                                        if (routeData && (event.key === 'Enter' || event.key === ' ')) {
                                                                            event.preventDefault();
                                                                            handleRouteDetailOpen();
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        display: 'inline-block',
                                                                        '&:hover': routeData ? { color: '#667eea', textDecoration: 'underline' } : undefined,
                                                                        '&:focus-visible': routeData ? { outline: '2px solid #667eea', outlineOffset: 2, borderRadius: 0.5 } : undefined
                                                                    }}
                                                                >
                                                                    <Typography component="div" sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
                                                                        {routeDisplayName}
                                                                    </Typography>
                                                                    <Typography component="div" variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                                                                        {getRouteStatusSubtitle()}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Start:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.route?.startLocation?.locationName || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                        {tripData?.route?.viaLocations?.length > 0 && tripData.route.viaLocations.map((viaLocation, viaIndex) => (
                                                            <Box key={viaIndex} component="tr" sx={{ display: 'table-row' }}>
                                                                <Box component="td" sx={{
                                                                    display: 'table-cell',
                                                                    verticalAlign: 'top',
                                                                    padding: '8px 0',
                                                                    fontWeight: 600,
                                                                    color: '#666',
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    Stop {viaIndex + 1}:
                                                                </Box>
                                                                <Box component="td" sx={{
                                                                    display: 'table-cell',
                                                                    verticalAlign: 'top',
                                                                    padding: '8px 0 8px 12px',
                                                                    wordBreak: 'break-word',
                                                                    lineHeight: 1.5,
                                                                    fontSize: '0.875rem'
                                                                }}>
                                                                    {viaLocation.locationName || 'N/A'}
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                End:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.route?.endLocation?.locationName || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Distance:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.route?.routeLength ? `${(tripData.route.routeLength).toFixed(1)} km` : 'N/A'}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card variant="outlined" sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ mb: 2, color: '#667eea' }}>
                                                    Timing Information
                                                </Typography>
                                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <Box component="tbody">
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                width: '35%',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Start Time:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripDateView(tripData?.actualStartTime || tripData?.plannedStartTime) || 'N/A'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                End Time:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.actualEndTime ? tripDateView(tripData?.actualEndTime) : 'Trip Incomplete'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Truck Distance:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.truckRunDistance ? `${(tripData.truckRunDistance).toFixed(1)} km` : '0 km'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Fuel Consumed:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.fuelConsumption ? `${(tripData.fuelConsumption).toFixed(1)} L` : '0 L'}
                                                            </Box>
                                                        </Box>
                                                        <Box component="tr" sx={{ display: 'table-row' }}>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0',
                                                                fontWeight: 600,
                                                                color: '#666',
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                Mileage:
                                                            </Box>
                                                            <Box component="td" sx={{
                                                                display: 'table-cell',
                                                                verticalAlign: 'top',
                                                                padding: '8px 0 8px 12px',
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.5,
                                                                fontSize: '0.875rem'
                                                            }}>
                                                                {tripData?.fuelEfficiency ? `${(tripData.fuelEfficiency).toFixed(1)} km/L` : '0 km/L'}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                    <Box sx={{ mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
                        {/* Significant Locations Section */}
                        {tripData?.significantLocations?.length > 0 && (
                            <Accordion defaultExpanded sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocationOnIcon sx={{ color: '#667eea' }} />
                                        <Typography variant="h6" sx={{ color: '#667eea' }}>
                                            Trip Locations ({tripData.significantLocations.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Entry Time</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Wait Duration</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Exit Time</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {tripData.significantLocations.map((loc, idx) => (
                                                    <TableRow key={loc._id || idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                                        <TableCell>{locTypeFormatter(loc.locationType)}</TableCell>
                                                        <TableCell>{loc.locationName}</TableCell>
                                                        <TableCell>{tripDateView(loc.entryTime)}</TableCell>
                                                        <TableCell>{tripDurationFormat(loc.dwellTime * 60)}</TableCell>
                                                        <TableCell>{loc.exitTime ? tripDateView(loc.exitTime) : '-'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Stops Section */}
                        <Accordion defaultExpanded={false} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <StopIcon sx={{ color: '#f093fb' }} />
                                    <Typography variant="h6" sx={{ color: '#f093fb' }}>
                                        All Stops ({tripData?.stops?.length || 0})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tripData?.stops?.map((stop, index) => (
                                                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                                    <TableCell>{tripDateView(stop.stopStartTime)}</TableCell>
                                                    <TableCell>{tripDateView(stop.stopEndTime)}</TableCell>
                                                    <TableCell>
                                                        <b>{tripHoursFormat(stop.hours)}</b>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                {stop.startAddress?.net_addr || stop.startAddress?.near || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>

                        {/* Rule Violations Section */}
                        {ruleViolations.length > 0 && (
                            <Accordion defaultExpanded={false} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BusAlertIcon sx={{ color: '#f5576c' }} />
                                        <Typography variant="h6" sx={{ color: '#f5576c' }}>
                                            Rule Violations ({ruleViolations.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={2}>
                                        {ruleViolations.map((violation, index) => {
                                            const stats = getRuleStats(violation);
                                            const ruleLabel = ruleNames[violation?.eventName] || violation?.eventName || 'Rule Violation';
                                            const locationLabel = violation?.eventLocation?.address?.net_addr
                                                || violation?.eventLocation?.address?.near
                                                || '-';
                                            const eventStart = violation?.eventStartTime ? tripDateView(violation.eventStartTime) : '-';
                                            const eventEnd = violation?.eventEndTime ? tripDateView(violation.eventEndTime) : '';
                                            const eventRange = eventEnd ? `${eventStart} to ${eventEnd}` : eventStart;
                                            const ruleIcon = ruleIconMap[violation?.eventName] || <BusAlertIcon sx={{ fontSize: 18, color: '#f5576c' }} />;

                                            return (
                                                <Paper
                                                    key={violation._id || index}
                                                    variant="outlined"
                                                    sx={{
                                                        p: { xs: 1.5, sm: 2 },
                                                        borderRadius: 2,
                                                        borderColor: '#f2c7d0',
                                                        background: 'linear-gradient(180deg, #ffffff 0%, #fff7f9 100%)',
                                                    }}
                                                >
                                                    <Stack spacing={1.5}>
                                                        <Stack
                                                            direction={{ xs: 'column', md: 'row' }}
                                                            spacing={1.5}
                                                            alignItems={{ xs: 'flex-start', md: 'center' }}
                                                            justifyContent="space-between"
                                                        >
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <Avatar
                                                                    sx={{
                                                                        width: 36,
                                                                        height: 36,
                                                                        backgroundColor: 'rgba(245,87,108,0.12)',
                                                                    }}
                                                                >
                                                                    {ruleIcon}
                                                                </Avatar>
                                                                <Box>
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2b2f38' }}>
                                                                            {ruleLabel}
                                                                        </Typography>
                                                                        {violation?.needsRecalc && (
                                                                            <Chip
                                                                                label="Recalc pending"
                                                                                size="small"
                                                                                sx={{
                                                                                    backgroundColor: 'rgba(245,87,108,0.12)',
                                                                                    color: '#d64e61',
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Stack>
                                                                    <Typography variant="body2" sx={{ color: '#6c7280' }}>
                                                                        {eventRange}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
                                                                {getHeaderChips(violation).map((chip) => (
                                                                    <Chip
                                                                        key={chip.key}
                                                                        label={chip.label}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: '#ffffff',
                                                                            border: '1px solid #f2c2c9',
                                                                            color: '#d64e61',
                                                                            fontWeight: 600,
                                                                        }}
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        </Stack>

                                                        <Divider sx={{ borderColor: 'rgba(245,87,108,0.12)' }} />

                                                        <Grid container spacing={1.5}>
                                                            {stats.map((stat) => (
                                                                <Grid item xs={12} sm={6} md={3} key={stat.label}>
                                                                    <Box
                                                                        sx={{
                                                                            p: 1.25,
                                                                            borderRadius: 1.5,
                                                                            border: '1px solid #f3dbe0',
                                                                            backgroundColor: '#ffffff',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                display: 'block',
                                                                                textTransform: 'uppercase',
                                                                                letterSpacing: '0.05em',
                                                                                color: '#8a9099',
                                                                                mb: 0.25,
                                                                            }}
                                                                        >
                                                                            {stat.label}
                                                                        </Typography>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2b2f38' }}>
                                                                            {stat.value}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            ))}
                                                        </Grid>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationOnIcon sx={{ fontSize: 16, color: '#888' }} />
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#5f6670' }}>
                                                                {locationLabel}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            );
                                        })}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Activity Log Section */}
                        {tripData?.significantEvents?.filter(event => event.eventType !== 'ruleViolation').length > 0 && (
                            <Accordion defaultExpanded={false} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <NotificationsActiveIcon sx={{ color: '#00875a' }} />
                                        <Typography variant="h6" sx={{ color: '#00875a' }}>
                                            Activity Log ({tripData?.significantEvents?.filter(event => event.eventType !== 'ruleViolation').length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {tripData?.significantEvents?.filter(event => event.eventType !== 'ruleViolation').map((event, index) => (
                                                    <TableRow key={event._id || index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                                        <TableCell>
                                                            {event?.eventTime ? tripDateView(event.eventTime) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={event?.eventName?.replace('Active Status Changed to ', '') || '-'}
                                                                size="small"
                                                                sx={{ backgroundColor: 'white', color: '#00875a' }}
                                                            />

                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                    {event?.eventLocation?.address?.net_addr || '-'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {/* Drives Section */}
                        <Accordion defaultExpanded={false} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DirectionsCarIcon sx={{ color: '#4facfe' }} />
                                    <Typography variant="h6" sx={{ color: '#4facfe' }}>
                                        Drives ({tripData?.drives?.length || 0})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Distance</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Avg Speed</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Top Speed</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Start Location</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>End Location</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tripData?.drives?.map((drive, index) => (
                                                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                                    <TableCell>{tripDateView(drive.driveStartTime)}</TableCell>
                                                    <TableCell>{tripDateView(drive.driveEndTime)}</TableCell>
                                                    <TableCell>
                                                        {drive.driveDuration}
                                                    </TableCell>
                                                    <TableCell>{drive.driveDistance ? `${drive.driveDistance.toFixed(1)} km` : 'N/A'}</TableCell>
                                                    <TableCell>{drive.averageSpeed ? `${drive.averageSpeed.toFixed(1)} km/h` : 'N/A'}</TableCell>
                                                    <TableCell>{drive.topSpeed ? `${drive.topSpeed.toFixed(1)} km/h` : 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                {drive.startAddress?.net_addr || drive.startAddress?.near ||  'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                {drive.endAddress?.net_addr || drive.endAddress?.near || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>

                        {/* Events Section */}
                        <Accordion defaultExpanded={false} sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EventIcon sx={{ color: 'SaddleBrown' }} />
                                    <Typography variant="h6" sx={{ color: 'SaddleBrown' }}>
                                        Events ({tripData?.vehicleEvents?.length || 0})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                <TableCell sx={{ fontWeight: 600, width: '50px' }}>Icon</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tripData?.vehicleEvents?.map((event, index) => (
                                                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        {eventIcons(event.event_desc, 0.8)}
                                                    </TableCell>
                                                    <TableCell>{tripDateView(event.event_time)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={event.event_desc}
                                                            size="small"
                                                            sx={{ backgroundColor: 'white', color: 'SaddleBrown' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                                {event.address?.net_addr || event.address?.near || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>


                    </Box>
                </Box>
                <RouteDetailView
                    open={routeDetailOpen}
                    onClose={handleRouteDetailClose}
                    route={routeForDetail}
                />
            </Paper>
        );
    }
};

export default TripReport;
